import React, { useState } from "react";
import { toast } from "react-toastify";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import Table from "../../../../../components/elements/table/Table";
import { setTitle } from "../../../../../helpers/MetaTag";
import { postDelete, postList, postOrdering, postStatusChange } from "../../../../../services/postService";
import ManageServiceDrawer from "../../../../../components/sections/admin/Service/Drawer/ManageServiceDrawer";
import Button from "../../../../../components/form/Button";
import { getPaginationRange, handleFilterChange, userLang } from "../../../../../helpers";
import { useDebounce } from "../../../../../hooks/useDebounce";
import DeleteModal from "../../../../../components/common/DeleteModal";
import Badge from "../../../../../components/elements/Badge";
import { truncateHtmlByWords } from "../../../../../util/Flag";
import TableControls from "../../../../../components/elements/TableControls";
import { status_filter } from "../../../../../components/constant/constant";
import Tooltip from "../../../../../components/elements/Tooltip";
import ActionBar from "../../../../../components/elements/ActionBar";
import TagFilter from "../../../../../components/elements/TagFilter";

const ServiceList = () => {
  setTitle("Service Management");
  const { t } = useTranslation();
  const [selectedItems, setSelectedItems] = useState([]);
  const [modalOpen, setModalOpen] = useState({ isOpen: false, data: null });
  const [keyword, setKeyword] = useState("");
  const search = useDebounce(keyword, 300);
  const queryClient = useQueryClient();
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    data: null,
  });
  const [filters, setFilters] = useState({
    limit: 100,
    page: 1,
    sortQuery: "ordering",
  });
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [selectFilterItem, setSelectFilterItem] = useState();
  const [selectedAllFilters, setSelectedAllFilters] = useState([]);

  const { data, error, isLoading, updateData, isFetching, refetch } = useQuery({
    queryKey: ["service", filters, search],
    queryFn: async () => {
      const res = await postList({
        ...filters,
        type: "service",
        ...(search && { keyword: search }),
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs;
    },
  });

  //   Service status change handler =================>
  const { mutate: statusChangeHandler, isPending: statusChangeInPending } = useMutation({
    mutationFn: async ({ id, status }) => {
      queryClient.setQueryData(["service", filters, search], (state) => {
        if (!state) return;

        const updatedData = state?.data?.map((item) =>
          item?._id === id
            ? {
              ...item,
              status: item.status === "active" ? "inactive" : "active",
            }
            : item
        );

        return { ...state, data: updatedData };
      });
      const res = await postStatusChange({ ids: [id], status });

      return { res, payload: { id, status } };
    },

    onSuccess: ({ res, payload }) => {
      if (res?.status === 200) {
        toast.success("Service status change successfully.");
      } else {
        queryClient.setQueryData(["service", filters, search], (state) => {
          if (!state) return;

          const revertedData = state?.data?.map((item) =>
            item?._id === payload.id
              ? {
                ...item,
                status: item.status === "active" ? "inactive" : "active",
              }
              : item
          );

          return { ...state, data: revertedData };
        });

        toast.error(res?.message || "Failed to update status.");
      }
    },

    onError: (error) => {
      toast.error(error?.message || "Something went wrong while updating status.");
      console.error("Status update error:", error);
    },
  });

  const tableHeadData = [
    {
      _id: 1,
      name: "Image",
      align: "left",
    },
    {
      _id: 2,
      name: "Title",
      align: "left",
      isSort: true,
      onSort: (val) => {
        if (val === "DESC") {
          setFilters((pre) => ({ ...pre, sortQuery: "-title" }));
        } else {
          setFilters((pre) => ({ ...pre, sortQuery: "title" }));
        }
      },
    },
    {
      _id: 3,
      name: "Content",
      align: "left",
    },
    {
      _id: 4,
      name: "Status",
      align: "left",
      isFilter: true,
      isFilterSearch: false,
      onFilter: (val) => {
        if (val?.length > 0 && val?.length === 1) {
          setFilters((pre) => ({
            ...pre,
            status: val?.map((item) => item?.value),
            page: 1,
          }));
        } else {
          setFilters((pre) => {
            delete pre?.status;
            return { ...pre };
          });
        }
      },
      filterData: status_filter || [],
    },
    {
      _id: 5,
      name: "Action",
      align: "left",
    },
  ];

  // Table Body Data =====================>
  const tableData = data?.data?.map((item) => {
    return {
      _id: item?._id,
      rowData: [
        {
          _id: 1,
          type: "user",
          align: "left",
          cellClass: "!min-w-[100px]",
          image: item?.coverMedia?.url,
        },
        {
          _id: 2,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: <div dangerouslySetInnerHTML={{ __html: item?.content?.[userLang]?.title || "-" }}></div> || "-",
        },
        {
          _id: 3,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: (
            <Tooltip
              position="bottom"
              className=""
              message={
                <div
                  className="text-[12px] whitespace-pre-wrap max-w-xs"
                  dangerouslySetInnerHTML={{
                    __html: truncateHtmlByWords(item?.content?.[userLang]?.content, 60),
                  }}
                ></div>
              }
            >
              <div
                className="flex gap-2 items-center  cursor-pointer"
                dangerouslySetInnerHTML={{
                  __html: truncateHtmlByWords(item?.content?.[userLang]?.content, 60),
                }}
              ></div>
            </Tooltip>
          ),
        },
        {
          _id: 4,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: (
            <div
              className="flex gap-2 items-center cursor-pointer"
              onClick={() => {
                statusChangeHandler({
                  id: item?._id,
                  status: item?.status === "active" ? "inactive" : "active",
                });
              }}
            >
              <Badge type={item?.status === "active" ? "success" : "danger"} label={item?.status === "active" ? "Active" : "Inactive"} />
            </div>
          ),
        },
        {
          _id: 5,
          type: "action",
          align: "left",
          cellClass: "w-[5%]",
          actionData: [
            {
              _id: 1,
              name: "Edit",
              icon: "fa-regular fa-pen-to-square",
              onClick: () => {
                setModalOpen({ isOpen: true, data: item });
              },
              standout: false,
            },

            {
              _id: 2,
              name: "Delete",
              icon: "fa-regular fa-trash-can",
              standout: true,
              onClick: () => {
                setDeleteModal({
                  isOpen: true,
                  data: item,
                });
              },
            },
          ],
        },
      ],
    };
  });

  // add or edit Service =================>
  function serviceManageResponse(res) {
    if (res?.status === 200 || res?.status === 201) {
      if (res?.status === 201) {
        if (filters?.page === 1) {
          queryClient.setQueryData(["service", filters, search], (state) => {
            if (state) {
              let stateData = JSON.parse(JSON.stringify(state?.data));
              stateData = [...stateData, res?.data];
              return { ...state, data: stateData };
            }
          });
          if (data?.metadata?.totalDocs > filters?.limit) {
            queryClient.invalidateQueries(["service", filters, search]);
          }
        } else {
          setFilters((pre) => ({ ...pre, page: 1 }));
        }
      }
      if (res?.status === 200) {
        queryClient.setQueryData(["service", filters, search], (state) => {
          if (state) {
            let stateData = JSON.parse(JSON.stringify(state?.data));
            stateData = stateData?.map((item) => (item?._id === res?.data?._id ? { ...item, ...res?.data } : item));
            return { ...state, data: stateData };
          }
        });
        if (data?.metadata?.totalDocs > filters?.limit) {
          queryClient.invalidateQueries(["service", filters, search]);
        }
      }
      setModalOpen({ isOpen: false, data: null });
    } else {
      toast.warning(res?.message || res?.error);
    }
  }

  //   Service delete handler =================>
  const { mutate: deleteHandler, isPending: deletePending } = useMutation({
    mutationFn: async (payload) => {
      const res = await postDelete({
        ids: Array.isArray(payload) ? payload : [payload],
        categoryType: "service",
      });
      return { res, payload: Array.isArray(payload) ? payload : [payload] };
    },
    onSuccess: ({ res, payload }) => {
      if (res?.status === 200) {
        setDeleteModal({ isOpen: false, data: null });
        setSelectedItems([]);
        queryClient.setQueryData(["service", filters, search], (state) => {
          if (state) {
            let stateData = JSON.parse(JSON.stringify(state?.data));
            stateData = (stateData || [])?.filter((item) => !payload?.includes(item?._id));
            return { ...state, data: stateData };
          }
        });

        if (data?.metadata?.totalDocs > filters?.limit) {
          queryClient.invalidateQueries(["service", filters, search]);
        }
      } else {
        toast.warning(res?.message || res?.error);
      }
    },
    onError: (error) => {
      console.log(error);
    },
  });

  async function serviceOrderingHandler(newData, oldData) {
    const ordering_data = newData?.map((item, i) => ({
      id: item?._id,
      ordering: i,
      categoryType: "service",
    }));
    const res = await postOrdering({ ordering_data });
    if (res?.status !== 200) {
      queryClient.setQueryData(["service", filters, search], (state) => {
        if (state) {
          return { ...state, data: oldData };
        }
      });
      toast.warning(res?.message || res?.error);
    }
  }

  const options = [
    {
      id: "serviceDelete",
      icon: "fa-regular fa-trash-can",
      tooltip: "Service Delete",
    },
  ];

  function actionHandler(val) {
    switch (val) {
      case "serviceDelete":
        setDeleteModal({
          isOpen: true,
          data: { _id: selectedItems },
        });
        break;

      default:
        break;
    }
  }

  return (
    <div className="space-y-4 w-full flex flex-col pb-10">
      <div className="space-y-8">
        {/* {selectedAllFilters?.length > 0 && (
          <div className="flex gap-2 items-center flex-wrap mt-2">
            {selectedAllFilters?.map((item) => (
              <div
                key={item}
                className="flex items-center gap-2 bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium capitalize">
                {item}
                <button
                  onClick={() => {
                    setSelectedAllFilters((prev) => prev.filter((i) => i !== item));
                    setSelectedFilter(null);
                    setSelectFilterItem({});
                  }}
                  className="text-blue-600 hover:text-red-500 focus:outline-none">
                  ×
                </button>
              </div>
            ))}
          </div>
        )} */}
        <div className="flex items-center gap-3">
          <TableControls
            limit={filters?.limit}
            totalDocs={data?.metadata?.totalDocs}
            onLimitChange={(val) => handleFilterChange("limit", val, setFilters)}
            onSearch={(val) => {
              setKeyword(val);
              handleFilterChange("page", 1, setFilters);
            }}
          />
          {/* <TagFilter
            type={"service"}
            selectedFilter={selectedFilter}
            setSelectedFilter={setSelectedFilter}
            setSelectFilterItem={setSelectFilterItem}
            setSelectedAllFilters={setSelectedAllFilters}
            filterOptions={[
              { name: "Status", value: "status" },
              // { name: "Date Range", value: "dateRange" },
              // { name: "Category", value: "category" },
              // { name: "Tags", value: "tags" },
            ]}
          /> */}
          {selectedItems?.length > 0 && (
            <div className="flex items-center gap-2">
              <ActionBar options={options} onChange={actionHandler} />
              <span className="flex items-center rounded-md bg-secondary text-surface-01 text-xs px-1 py-1 gap-1">
                <span className="inline-block px-1">{selectedItems?.length} Selected</span>
                <button onClick={() => setSelectedItems([])} type="button" className="cursor-pointer">
                  <i className="fa-fw fa-light fa-xmark"></i>
                </button>
              </span>
            </div>
          )}
          <div className="relative ml-auto">
            <Button buttonClasses={""} buttonLabel={"Create New"} buttonIcon={"fa-regular fa-plus"} buttonIconPosition="left" buttonLabelClasses={"font-medium !text-sm"} buttonFunction={() => setModalOpen({ isOpen: true, data: null })} />
          </div>
        </div>
      </div>

      <Table
        tableHeadData={tableHeadData}
        tableData={tableData}
        totalDataCount={data?.metadata?.totalDocs}
        isLoder={isLoading}
        pageCount={data?.metadata?.totalPages}
        currentPage={filters?.page}
        onPageChange={(val) => {
          handleFilterChange("page", val, setFilters);
          setSelectedItems([]);
        }}
        handleOrdering={(val) => {
          console.log(val);
        }}
        isFetching={isFetching}
        isOrderingDisabled={true}
        selectable
        selectedItems={selectedItems}
        setSelectedItems={setSelectedItems}
        tableActionDropdownContainer={"!w-48"}
        showingData={getPaginationRange(filters?.page, filters?.limit, data?.metadata?.totalDocs)}
      />

      <ManageServiceDrawer open={modalOpen?.isOpen} data={modalOpen?.data} close={() => setModalOpen({ isOpen: false, data: null })} getResponse={serviceManageResponse} />
      <DeleteModal
        open={deleteModal?.isOpen}
        loading={deletePending}
        onClose={() => setDeleteModal((pre) => ({ data: null, isOpen: false }))}
        deleteHandler={() => {
          deleteHandler(deleteModal?.data?._id);
        }}
      />
    </div>
  );
};

export default ServiceList;
